import { Button, Container, ContainerScroll } from 'geniebook-ui/dist';
import React, { ReactNode, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import NavBar from '@components/navigation/NavBar';
import './ErrorPage.css';
import { CDN_URL } from '@utils/constants/url.constants';
import { useStore } from 'jotai';
import {
  haveAccountSection,
  haveBackButton,
  haveBottomNavbar,
  haveFullLogo,
  haveLogoutButton,
  haveSubjectMenu,
  haveTitle,
  haveTopNavbar,
  resetToDefaultNavbarState,
} from '@atom/navbarAtom';

interface ErrorPageProps {
  code?: string;
  showNavBar?: boolean;
  customCopy?: {
    header: string | ReactNode;
    body: string | ReactNode;
  };
}

function ErrorPage(props: ErrorPageProps) {
  const store = useStore();
  useEffect(() => {
    if (props.code !== '099-03') {
      store.set(haveTopNavbar, false);
      store.set(haveBottomNavbar, false);
      store.set(haveBackButton, false);
      store.set(haveSubjectMenu, false);
      store.set(haveTitle, false);
      store.set(haveFullLogo, true);
      store.set(haveLogoutButton, false);
      store.set(haveAccountSection, false);
    }

    return () => {
      resetToDefaultNavbarState();
    };
  }, []);

  const moment = require('moment');

  const navigate = useNavigate();

  const [showAccountDropdown, setShowAccountDropdown] = React.useState(false);
  const [isShowMobileBubbleDropdown, setIsShowMobileBubbleDropdown] =
    React.useState<boolean>(false);

  const [expiryDate, setExpiryDate] = React.useState<string>();

  const params = useParams();

  React.useEffect(() => {
    const tmpDelayedStartDate = moment(params.delayedStartDate);
    if (params.delayedStartDate && !!tmpDelayedStartDate.valueOf())
      setExpiryDate(tmpDelayedStartDate.format('D MMM YYYY'));
  }, [params.delayedStartDate]);

  if (!props.code) props.code = '404';

  const titles: { [key: string]: string | undefined } = {
    '404': "We can't find your page :(",
    '500': 'Something went wrong :(', // internal server error
    '099-03': 'Your subscription starts on ' + expiryDate, // Delayed Start Subscription
    '103-09': 'Link Expired', // Link Expired
    'no-subscription': 'No trial or subscription was found', // No Subscription
    'no-student-data': 'Registration success!', // When student created from app/school/[school_code] but not assigned to any worksheet yet
  };

  const desc: { [key: string]: React.ReactNode | undefined } = {
    '404': 'Check your URL, or return to the homepage and try again.',
    '500':
      "We're fixing our servers as fast as we can. Take a break and try again later.",
    '099-03': (
      <div>
        We look forward to seeing you then! If there is a discrepancy, kindly{' '}
        <a
          href="https://geniebook.com/contact"
          target="_blank"
          rel={'noreferrer'}
          className={'underline'}
        >
          contact our support team.
        </a>
      </div>
    ),
    '103-09':
      'The link you are trying to access is no longer valid. Please request a new one.',
    'no-subscription':
      "We couldn't find a trial or subscription linked to this account. Please link a subscription or contact Geniebook help for assistance.",
    'no-student-data':
      'Hang tight, Geniebook will create an assessment for you over the next few days',
  };

  return (
    <div
      className={`
        error_container
        bg-cover
        bg-fixed
        bg-no-repeat
        bg-primary-navyblue-dark
        bg-[45%_75%]
        bg-[url('/public/img/Background_Special.svg')]
        flex
        flex-col
        h-full
        w-full ${
          props.code === 'no-subscription' ||
          (props.showNavBar !== undefined && !props.showNavBar)
            ? '!h-[calc(100vh-68px)]'
            : ''
        }`}
    >
      {props.code === 'no-subscription' ||
      (props.showNavBar !== undefined && !props.showNavBar) ? null : (
        <NavBar
          showAccountDropdown={showAccountDropdown}
          setShowAccountDropdown={setShowAccountDropdown}
          isShowMobileBubbleDropdown={isShowMobileBubbleDropdown}
          setIsShowMobileBubbleDropdown={setIsShowMobileBubbleDropdown}
        />
      )}
      <ContainerScroll
        customScrollBarProps={{
          className:
            'error_content justify-center grow overflow-hidden text-center text-neutral-whiteboard',
        }}
      >
        <Container
          className="error_content_container
            py-0
            items-center
            flex
            flex-row-reverse
            min-h-full
            max-lg:flex-col
            max-lg:justify-center
          "
        >
          <img
            className="w-[calc(100%*512/1099)]
              max-lg:w-[calc(100%*347/744)]
              max-md:w-[calc(100%*266/375)]"
            src={`${CDN_URL}assets/images/Illustration Error Page.svg`}
          />
          <div
            className={`self-center
              w-[calc(100%*560/1099)]
              lg:mr-[27px]
              max-lg:w-full ${
                props.code === 'no-subscription' ? '!max-w-[560px]' : ''
              }`}
          >
            <div
              className={`error_title font-bold
                leading-10
                text-xxl
                max-lg:mt-6
                max-md:leading-8
                max-md:text-xl ${
                  props.code === 'no-subscription'
                    ? '!max-w-[450px] mx-auto'
                    : ''
                }`}
            >
              {props.customCopy?.header ?? titles[props.code ?? '404']}
            </div>
            <div
              className={`error_desc leading-7
                mt-4
                text-lg
                max-md:leading-6
                max-md:text-md ${props.code === 'no-subscription' ? '' : ''}`}
            >
              {props.customCopy?.body ?? desc[props.code ?? '404']}
            </div>
            {(!props.code || props.code == '404') && (
              <Button
                className="error_btn mt-8
                  mx-auto
                  whitespace-nowrap
                  w-[258px]
                  max-md:max-w-full
                  max-md:w-full"
                onClick={() => {
                  navigate(`/`);
                }}
              >
                Back to home
              </Button>
            )}
            {(!props.code || props.code == '103-09') && (
              <Button
                className="error_btn mt-8
                  mx-auto
                  whitespace-nowrap
                  w-[258px]
                  max-md:max-w-full
                  max-md:w-full"
                onClick={() => {
                  navigate(`/forgot-password`);
                }}
              >
                Request new password
              </Button>
            )}
          </div>
          {props.code === 'no-subscription' ? (
            <div
              className="lg:hidden w-full h-[68px] self-center
                lg:mr-[27px]
                max-lg:w-full"
            />
          ) : null}
        </Container>
      </ContainerScroll>
    </div>
  );
}

export default ErrorPage;
